import React, { useState } from 'react';
import { X } from 'lucide-react';
import Grid from '@mui/material/Grid';
import { useTheme } from '@mui/material/styles';

const ReportConfigModal = ({ reportConfig, setReportConfig, onClose, onApply }) => {
  const theme = useTheme();
  const showBudgetOption = reportConfig.reportCategory === 'sales';
  const [dateError, setDateError] = useState('');

  const getYear = (date) =>
    date ? new Date(date).getFullYear() : null;

  const handleApply = () => {
    const { start, end } = reportConfig.dateRange;

    if (!start || !end) {
      setDateError('Please select both start date and end date');
      return;
    }

    const startYear = getYear(start);
    const endYear = getYear(end);

    if (startYear !== endYear) {
      setDateError('Date range must be within the same year');
      return;
    }

    if (new Date(end) < new Date(start)) {
      setDateError('End date cannot be before start date');
      return;
    }

    setDateError('');
    onApply();
  };

  const isDark = theme.palette.mode === 'dark';
  const calendarIconFilter = isDark
    ? 'brightness(0) invert(1)'
    : 'brightness(0) invert(0.4)';

  return (
    <div className="absolute top-0 right-0 h-full shadow-xl flex justify-end z-40">

      <style>{`
        input[type="date"]::-webkit-calendar-picker-indicator {
          filter: ${calendarIconFilter};
          opacity: 1;
          cursor: pointer;
        }
      `}</style>

      <div
        className="w-full sm:w-[400px] h-full overflow-y-auto border-l transition-all duration-300"
        style={{ backgroundColor: theme.palette.background.paper, borderColor: theme.palette.divider }}
      >
        <Grid container spacing={3} className="p-5">

          <Grid item xs={12}>
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold" style={{ color: theme.palette.text.primary }}>Reports</h2>
              <button onClick={onClose} className="hover:opacity-75" style={{ color: theme.palette.text.secondary }}>
                <X size={20} />
              </button>
            </div>
          </Grid>

          <Grid item xs={12}>
            <div className="rounded-full p-2 flex" style={{ backgroundColor: theme.palette.background.default }}>
              {['sales', 'purchase'].map(type => {
                const isActive = reportConfig.reportCategory === type;

                return (
                  <button
                    key={type}
                    onClick={() =>
                      setReportConfig({
                        ...reportConfig,
                        reportCategory: type,
                        reportType: 'sales_report',
                        budgetType: type === 'sales' ? (reportConfig.budgetType || 'internal') : '',
                        printMainAccountOnly: 'no',
                        dateRange: {
                          start: '',
                          end: '',
                        },
                      })
                    }
                    className="flex-1 rounded-full text-base p-2 font-medium transition-all"
                    style={{
                      backgroundColor: isActive ? theme.palette.secondary.main : 'transparent',
                      color: isActive ? theme.palette.secondary.contrastText : theme.palette.text.primary,
                      boxShadow: isActive ? 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.06)' : 'none'
                    }}
                  >
                    {type === 'sales' ? 'Sales Report' : 'Purchase Report'}
                  </button>
                );
              })}
            </div>
          </Grid>

          <Grid item xs={12}>
            <fieldset className="border rounded-md px-4 pb-3" style={{ borderColor: theme.palette.divider }}>
              <legend className="text-xs px-1" style={{ color: theme.palette.text.secondary }}>Report Type</legend>
              <div className="relative flex items-center">
                <select
                  className="w-full bg-transparent text-md outline-none p-1 pr-8 appearance-none"
                  style={{ color: theme.palette.text.primary }}
                  value={reportConfig.reportType}
                  onChange={(e) =>
                    setReportConfig({ ...reportConfig, reportType: e.target.value })
                  }
                >
                  <option value="sales_report" style={{ backgroundColor: theme.palette.background.paper, color: theme.palette.text.primary }}>
                    {reportConfig.reportCategory === 'sales' ? 'Sales Report' : 'Purchase Report'}
                  </option>
                  <option value="sales_report_with_details" style={{ backgroundColor: theme.palette.background.paper, color: theme.palette.text.primary }}>
                    {reportConfig.reportCategory === 'sales'
                      ? 'Sales Report with Project Details'
                      : 'Purchase Report with Project Details'}
                  </option>
                </select>
                <span className="pointer-events-none absolute right-1 border-t-8 border-x-8 border-x-transparent" style={{ borderTopColor: theme.palette.text.secondary }} />
              </div>
            </fieldset>
          </Grid>

          <Grid item xs={12}>
            <fieldset className="border rounded-lg px-4 pb-3" style={{ borderColor: theme.palette.divider }}>
              <legend className="text-xs px-1" style={{ color: theme.palette.text.secondary }}>History</legend>
              <div className="relative flex items-center">
                <select
                  className="w-full bg-transparent text-md outline-none p-1 pr-8 appearance-none"
                  style={{ color: theme.palette.text.primary }}
                  value={reportConfig.history}
                  onChange={(e) =>
                    setReportConfig({ ...reportConfig, history: e.target.value })
                  }
                >
                  <option value="actual_loading_date" style={{ backgroundColor: theme.palette.background.paper, color: theme.palette.text.primary }}>Actual Loading Date</option>
                  <option value="loading_plan_date" style={{ backgroundColor: theme.palette.background.paper, color: theme.palette.text.primary }}>Loading Plan Date</option>
                </select>
                <span className="pointer-events-none absolute right-1 border-t-8 border-x-8 border-x-transparent" style={{ borderTopColor: theme.palette.text.secondary }} />
              </div>
            </fieldset>
          </Grid>

          <Grid item xs={12}>
            <fieldset className="border rounded-lg px-4 pb-4" style={{ borderColor: theme.palette.divider }}>
              <legend className="text-xs px-1" style={{ color: theme.palette.text.secondary }}>Date Range</legend>

              <div className="space-y-4 mt-1">
                <div className="border rounded-lg px-3 py-2" style={{ borderColor: theme.palette.divider }}>
                  <label className="text-xs block mb-1" style={{ color: theme.palette.text.secondary }}>
                    Start-Date
                  </label>
                  <input
                    type="date"
                    className="w-full bg-transparent outline-none"
                    style={{
                      color: theme.palette.text.primary,
                      colorScheme: theme.palette.mode,
                    }}
                    value={reportConfig.dateRange.start}
                    onChange={(e) => {
                      setDateError('');
                      setReportConfig({
                        ...reportConfig,
                        dateRange: {
                          ...reportConfig.dateRange,
                          start: e.target.value,
                        },
                      });
                    }}
                  />
                </div>

                <div className="text-center text-sm" style={{ color: theme.palette.text.secondary }}>to</div>

                <div className="border rounded-lg px-3 py-2" style={{ borderColor: theme.palette.divider }}>
                  <label className="text-xs block mb-1" style={{ color: theme.palette.text.secondary }}>
                    End-Date
                  </label>
                  <input
                    type="date"
                    className="w-full bg-transparent outline-none"
                    style={{
                      color: theme.palette.text.primary,
                      colorScheme: theme.palette.mode,
                    }}
                    value={reportConfig.dateRange.end}
                    min={reportConfig.dateRange.start || undefined}
                    max={
                      reportConfig.dateRange.start
                        ? `${new Date(reportConfig.dateRange.start).getFullYear()}-12-31`
                        : undefined
                    }
                    onChange={(e) => {
                      setDateError('');
                      setReportConfig({
                        ...reportConfig,
                        dateRange: {
                          ...reportConfig.dateRange,
                          end: e.target.value,
                        },
                      });
                    }}
                  />
                </div>
              </div>

              {dateError && (
                <p className="mt-2 text-xs" style={{ color: theme.palette.error.main }}>
                  {dateError}
                </p>
              )}
            </fieldset>
            <p className="mt-2 text-xs" style={{ color: theme.palette.text.disabled }}>
              Date range must be within the same year
            </p>
          </Grid>

          {reportConfig.reportType === 'sales_report_with_details' && (
            <Grid item xs={12}>
              <fieldset className="border rounded-lg px-4 pb-3" style={{ borderColor: theme.palette.divider }}>
                <legend className="text-xs px-1" style={{ color: theme.palette.text.secondary }}>
                  Print main account only?
                </legend>
                <div className="relative flex items-center">
                  <select
                    className="w-full bg-transparent text-md outline-none p-1 pr-8 appearance-none"
                    style={{ color: theme.palette.text.primary }}
                    value={reportConfig.printMainAccountOnly}
                    onChange={(e) =>
                      setReportConfig({
                        ...reportConfig,
                        printMainAccountOnly: e.target.value,
                      })
                    }
                  >
                    <option value="yes" style={{ backgroundColor: theme.palette.background.paper, color: theme.palette.text.primary }}>Yes</option>
                    <option value="no" style={{ backgroundColor: theme.palette.background.paper, color: theme.palette.text.primary }}>No</option>
                  </select>
                  <span className="pointer-events-none absolute right-1 border-t-8 border-x-8 border-x-transparent" style={{ borderTopColor: theme.palette.text.secondary }} />
                </div>
              </fieldset>
            </Grid>
          )}

          {showBudgetOption && (
            <Grid item xs={12}>
              <fieldset className="border rounded-lg px-4 pb-3" style={{ borderColor: theme.palette.divider }}>
                <legend className="text-xs px-1" style={{ color: theme.palette.text.secondary }}>Budget Type</legend>
                <div className="relative flex items-center">
                  <select
                    className="w-full bg-transparent text-md outline-none p-1 pr-8 appearance-none"
                    style={{ color: theme.palette.text.primary }}
                    value={reportConfig.budgetType}
                    onChange={(e) =>
                      setReportConfig({
                        ...reportConfig,
                        budgetType: e.target.value,
                      })
                    }
                  >
                    <option value="internal" style={{ backgroundColor: theme.palette.background.paper, color: theme.palette.text.primary }}>Internal</option>
                    <option value="external" style={{ backgroundColor: theme.palette.background.paper, color: theme.palette.text.primary }}>External</option>
                  </select>
                  <span className="pointer-events-none absolute right-1 border-t-8 border-x-8 border-x-transparent" style={{ borderTopColor: theme.palette.text.secondary }} />
                </div>
              </fieldset>
            </Grid>
          )}

          <Grid item xs={12} className="flex justify-end">
            <button
              onClick={handleApply}
              className="px-8 py-5 rounded-md text-sm font-medium transition-opacity hover:opacity-90"
              style={{ backgroundColor: theme.palette.secondary.main, color: theme.palette.secondary.contrastText }}
            >
              Apply Selection
            </button>
          </Grid>

        </Grid>
      </div>
    </div>
  );
};

export default ReportConfigModal;